import { Avatar, Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { useSelector } from "react-redux";
import GCBackButton from "../../../components/GreenCity/GCBackButton";
import SubmitButton from "../../../components/SubmitButton";
import Separator from "../../../components/Separator";
import LoadingModal from "../../../components/LoadingModal";
import MessageModal from "../../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../../components/ErrorMessageModal";
import profile from "../../../assets/images/profile_img.png";
import { styles } from "../../styles";
import { convertUserImage, getRole } from "../../../util/utils";
import appServices from "../../../services/app-services/appServices";

function GroupRoles(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { memberId } = location?.state || {};
  const [member, setMember] = useState<any>([]);
  const [role, setRole] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false)
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false)
  const [groupIcon, setGroupIcon] = useState<File>()

  useEffect(() => {
    apiCall()
  }, [])

  const apiCall = async () => {
    try {
      setLoading(true);
      const res = await appServices.findMember(memberId, groupInfo?.id)
      const resMember = res.member_group_user
      if (resMember) {
        setMember(resMember)
      } else {
        throw new Error()
      }
    } catch (error: any) {
      setMessage(`${T("group_roles_member_is_not_found")}`);
      setOpenMessageModal(true)
      setTimeout(() => {
        navigate(-1)
      }, 3000);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setRole(member?.role);

    appServices.getGroupIcon(groupInfo.id).then(blob => {
      const file = new File([blob], groupInfo.group_icon, { type: blob.type });
      setGroupIcon(file);
    }).catch(() => {
      console.error("Failed to get group icon image")
    });
  }, [groupInfo, member]);

  const handleRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRole(event.target.value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const body = {
        role: role
      }
      await appServices.updateMemberGroupUsers(member?.id, body);
      setMessage(`${T("group_roles_role_is_changed")}`);
      setOpenMessageModal(true)
      setTimeout(() => {
        navigate(-1)
      }, 3000);
      return true
    } catch (error: any) {
      setMessage(error.response?.data?.message ?? `${T("api_unknown_error")}`);
      setOpenErrorMessageModal(true)
      return false
    } finally {
      setLoading(false);
    }
  };

  const radioGroupContent = useMemo(() => {
    if (!role) return (<></>)

    return (
      <RadioGroup aria-labelledby="payment-radio-buttons-group-label" value={role} onChange={handleRole} name="radio-buttons-group">
        <FormControlLabel
          disabled
          value="leader"
          control={<Radio sx={{ alignSelf: "flex-start" }} />}
          label={
            <div>
              <Typography variant="body1">{T("leader")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {T("leader_description")}
              </Typography>
            </div>
          }
          sx={{ alignItems: "flex-start" }}
        />
        <Separator sx={{ marginTop: "10px", marginBottom: "10px" }} color="gray" />

        <FormControlLabel
          value="sub-leader"
          control={<Radio sx={{ alignSelf: "flex-start" }} />}
          label={
            <div>
              <Typography variant="body1">{T("sub_leader")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {T("subleader_description")}
              </Typography>
            </div>
          }
          sx={{ alignItems: "flex-start" }}
        />
        <Separator sx={{ marginTop: "10px", marginBottom: "10px" }} color="gray" />

        <FormControlLabel
          value="finance"
          control={<Radio sx={{ alignSelf: "flex-start" }} />}
          label={
            <div>
              <Typography variant="body1">{T("finance")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {T("finance_description")}
              </Typography>
            </div>
          }
          sx={{ alignItems: "flex-start" }}
        />
        <Separator sx={{ marginTop: "10px", marginBottom: "10px" }} color="gray" />

        <FormControlLabel
          value="member"
          control={<Radio sx={{ alignSelf: "flex-start" }} />}
          label={
            <div>
              <Typography variant="body1">{T("no_role")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {T("no_role_description")}
              </Typography>
            </div>
          }
          sx={{ alignItems: "flex-start" }}
        />
      </RadioGroup>
    );
  }, [role]);

  return (
    <div style={style.headerContainer}>
      <GCBackButton onClick={() => navigate(-1)} />
      <Box style={style.eventPostingBox}>
        <Box component="div" style={style.eventPostingIcon}>
          <Avatar
            src={groupIcon instanceof File ? URL.createObjectURL(groupIcon) : ''}
            sx={{ width: 30, height: 30 }}
          />
        </Box>
        <Typography style={{ fontWeight: "700", marginTop: "5px" }}>
          {groupInfo?.groupName}
        </Typography>
      </Box>
      <Box style={{ display: "flex", flexDirection: "row" }}>
        <Box component="div" style={style.eventPostingIcon}>
          <Avatar src={member?.user?.image !== null ? convertUserImage(member?.user?.image) : profile} sx={{ width: 30, height: 30 }} />
        </Box>
        <Typography style={{ fontWeight: "700", marginTop: "5px" }}>{member?.user?.nickname}</Typography>
      </Box>
      <Separator sx={{ marginTop: "10px", marginBottom: "10px" }} color="gray" />

      <Box style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
        <Box>
          <Typography style={{ fontSize: 10 }}>{T("real_name")}</Typography>
          <Typography>{member?.user?.name}</Typography>
        </Box>
        <Box>
          <Typography style={{ fontSize: 10 }}>{T("role")}</Typography>
          <Typography>{getRole(member?.role)}</Typography>
        </Box>
        <Box>
          <Typography style={{ fontSize: 10 }}>{T("role_updated_date")}</Typography>
          <Typography>{member?.updated_at?.split("T")[0]}</Typography>
        </Box>
      </Box>

      <Separator sx={{ marginTop: "10px", marginBottom: "10px" }} color="gray" />

      <Box>{radioGroupContent}</Box>
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SubmitButton
          color="secondary"
          style={{
            ...styles.introSliderBtn,
            marginTop: 40,
            width: 220,
          }}
          size={"large"}
          variant="contained"
          endIcon={<ArrowForwardIosIcon />}
          validate={async () => true}
          onSubmit={handleSave}
        >
          <span style={{ paddingRight: 30, paddingLeft: 40 }}>{T("save")}</span>
        </SubmitButton>
        <Box style={{ height: 100 }}></Box>
      </Box>
      <LoadingModal visible={loading} />
      <MessageModal
        message={message}
        open={openMessageModal}
        onClose={() => setOpenMessageModal(false)}
        showButton={false}
      />
      <ErrorMessageModal
        messages={[message]}
        open={openErrorMessageModal}
        onClose={() => setOpenErrorMessageModal(false)}
        showButton={false}
      />
    </div>
  );
}

export default GroupRoles;

const style = {
  eventPostingIcon: {
    borderRadius: "50%",
    padding: "3px 6px 3px 6px",
    color: "white",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  headerContainer: {
    paddingTop: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: "12px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
  },
};
